import * as React from 'react';

const s =
  require('./LightboxViewer.scss').default ?? require('./LightboxViewer.scss');

import classnames from 'classnames';
import { X } from 'wix-ui-icons-common';
import {
  CLOSE_TYPES_BI_EVENT,
  LightboxTriggers,
  LightboxViewerProps,
  LightboxViewerState,
} from '../../interfaces';
import i18n from '../../i18n';
import { I18nextProvider } from 'react-i18next';
import { CmsDataStore } from '@wix/dealer-store/dist/src/CmsDataStore';
import { BiLogger } from '@wix/dealer-common/dist/src/services/BiLogger';
import {
  CmsStoreInitData,
  BannerData,
  OnClickParams,
} from '@wix/dealer-common/dist/src/interfaces';
import {
  VIEW_EVENT,
  CLICK_EVENT,
} from '@wix/dealer-common/dist/src/services/BiLogger/bi-events';
import { CmsElements } from '@wix/dealer-core/dist/src/components/CmsElements/CmsElements';
import { reportSentryError } from '@wix/dealer-common/dist/src/services/SentryReporter/SentryReporter';

export class LightboxViewer extends React.Component<
  LightboxViewerProps,
  LightboxViewerState
> {
  private wrapper: HTMLElement;
  private readonly cmsDataStore: CmsDataStore;
  private readonly triggers: LightboxTriggers;
  private readonly biLogger: BiLogger;

  constructor(props: any) {
    super(props);
    this.biLogger = new BiLogger();
    const cmsInitData = this.getCmsInitData();
    this.cmsDataStore = new CmsDataStore(cmsInitData);
    this.triggers = JSON.parse(this.props.lightbox.lightboxTriggers);
    this.state = {
      shouldShow: false,
      fadeOut: false,
      cmsStoreLoaded: true,
      lightboxClosed: false,
    };
  }

  getCmsInitData(): CmsStoreInitData {
    const {
      bannerPosition,
      metasiteGuid,
      locale,
      translationsUrl,
      onClose,
      lightbox,
      signedInstance,
    } = this.props;
    const onViewerClose = this.onViewerClose(onClose);

    const banners = {
      ...lightbox,
      bannerTemplate: JSON.parse(lightbox.bannerTemplate),
    };
    return {
      banners,
      bannerPosition,
      metasiteGuid,
      locale,
      onClose: onViewerClose,
      translationsUrl,
      biLogger: this.biLogger,
      signedInstance,
    };
  }

  onLightboxClick = (onClick: Function) => (clickParams: OnClickParams) => {
    this.handleCloseByClickOnButton();
    if (onClick) {
      onClick(clickParams);
    }
  };

  onViewerClose = (onClose?: Function) => (closeType: string) => {
    this.closeLightbox();
    if (!this.state.lightboxClosed) {
      if (closeType) {
        this.reportClose(closeType);
      }
      if (onClose) {
        onClose();
      }
    }
    this.setState({ lightboxClosed: true });
  };

  onLightboxView = (onView?: Function) => (aboveTheFold: boolean) => {
    this.logBiView(aboveTheFold);
    if (onView) {
      onView();
    }
  };

  logBiView = (aboveTheFold: boolean) => this.log(VIEW_EVENT, { aboveTheFold });

  async componentDidMount() {
    const { delayOpenSec } = this.triggers;
    document.addEventListener('keydown', this.handleKeydown);
    setTimeout(this.showLightbox, delayOpenSec * 1000 || 0);
  }

  componentDidUpdate() {
    if (this.wrapper) {
      this.wrapper.focus();
    }
  }

  handleKeydown = ({ key }: KeyboardEvent) => {
    const onLightboxClose = this.cmsDataStore.getOnClose();
    if (key === 'Escape' && !this.state.lightboxClosed) {
      onLightboxClose(CLOSE_TYPES_BI_EVENT.CLOSE_ESC);
    }
  };

  showLightbox = () => {
    const { ttlSecAnyLB, ttlSecSameLB } = this.triggers;
    const lightboxGuid = this.props.lightbox.lightboxGuid;
    this.setState({ shouldShow: true });
    return this.props.lightboxStorage
      .saveLightbox({
        ttlSecSameLB,
        ttlSecAnyLB,
        lightboxGuid,
        lastSeen: Date.now(),
      })
      .catch(e =>
        reportSentryError(e, {
          realEstateId: this.props.bannerPosition,
          msId: this.props.metasiteGuid,
          reportingClass: 'LightboxViewer',
        }),
      );
  };

  handleBackgroundClick = (event: any) => {
    const onLightboxClose = this.cmsDataStore.getOnClose();
    if (
      this.wrapper &&
      event.target === this.wrapper &&
      !this.state.lightboxClosed
    ) {
      onLightboxClose(CLOSE_TYPES_BI_EVENT.CLOSE_BACKGROUND);
    }
  };

  reportClose = (clickType: string) => {
    this.reportCloseEvent(
      this.cmsDataStore.getLocale(),
      this.cmsDataStore.getMetaSiteId(),
      this.cmsDataStore.getBannerRoot(),
      this.cmsDataStore.getBannerPosition(),
      clickType,
    );
  };

  closeLightbox = () => {
    this.setState({ fadeOut: true });
    window.removeEventListener('focus', this.closeLightbox);
    document.removeEventListener('keydown', this.handleKeydown);
    return setTimeout(() => this.setState({ shouldShow: false }), 1500);
  };

  lightboxWrapperClassnames = (
    isWhiteBackground: boolean,
    isBlackBackground: boolean,
  ) => ({
    [s.whiteOverlay]: isWhiteBackground,
    [s.blackOverlay]: isBlackBackground,
    [s.fadeOut]: this.state.fadeOut,
  });

  log = (eventId: number, additionalParams: { [key: string]: any } = {}) =>
    this.biLogger.log(
      eventId,
      false,
      additionalParams,
      this.cmsDataStore.getLocale(),
      this.cmsDataStore.getMetaSiteId(),
      this.cmsDataStore.getBannerRoot(),
      this.cmsDataStore.getBannerPosition(),
    );

  handleCloseByClickOnButton = () => {
    const onLightboxClose = this.cmsDataStore.getOnClose();
    window.addEventListener('focus', () => onLightboxClose(null));
  };

  reportCloseEvent = (
    locale: string,
    metasiteId: string,
    bannerRoot: BannerData,
    bannerPosition: string,
    clickType?: string,
  ) => {
    this.biLogger.log(
      CLICK_EVENT,
      false,
      { clickType },
      locale,
      metasiteId,
      bannerRoot,
      bannerPosition,
    );
  };

  render() {
    const onLightboxView = this.onLightboxView(this.props.onView);
    const onLightboxClick = this.onLightboxClick(this.props.onClick);
    const onLightboxClose = this.cmsDataStore.getOnClose();
    const { shouldShow, cmsStoreLoaded, fadeOut } = this.state;
    const whiteBG = this.triggers.displayStyle === 'white-lb';
    const blackBG = this.triggers.displayStyle === 'black-lb';

    return shouldShow && cmsStoreLoaded ? (
      <div
        className={classnames(
          s.lightboxOverlay,
          this.lightboxWrapperClassnames(whiteBG, blackBG),
        )}
        onClick={this.handleBackgroundClick}
        ref={(node: any) => (this.wrapper = node)}
        data-hook="lightbox-wrapper"
        role="dialog"
        tabIndex={-1}
      >
        {whiteBG && (
          <button
            data-hook="close-button"
            aria-label="close"
            className={classnames(s.closeButton, { [s.fadeOut]: fadeOut })}
            onClick={this.closeLightbox}
          >
            <X />
          </button>
        )}
        <div
          data-hook="lightbox-section"
          className={classnames(s.lightboxModal, {
            [s.fadeOut]: this.state.fadeOut,
          })}
        >
          <CmsElements
            dealerOptions={this.props.dealerOptions}
            bannerRoot={this.cmsDataStore.getBannerRoot()}
            cmsElementContext={this.cmsDataStore.getContext()}
            pageSettings={this.cmsDataStore.pageSettings}
            metasiteId={this.cmsDataStore.getMetaSiteId()}
            locale={this.cmsDataStore.getLocale()}
            bannerPosition={this.cmsDataStore.getBannerPosition()}
            cmsElementItems={this.cmsDataStore.cmsElementItems()}
            onView={onLightboxView}
            onClose={onLightboxClose}
            onClick={onLightboxClick}
            biLogger={this.biLogger}
          />
        </div>
      </div>
    ) : null;
  }
}

export const LightboxViewerWithTranslations = (props: LightboxViewerProps) => {
  return (
    <I18nextProvider
      i18n={i18n({
        locale: props.locale,
        baseUrl: props.translationsUrl,
      })}
    >
      <LightboxViewer {...props} />
    </I18nextProvider>
  );
};
